import React from "react";
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CustomYAxisTick } from "./custom-y-axis-tick";

export interface TopProvisionedItem {
  name: string;
  totalQuantity: number;
  provisionCount: number;
  unit?: string | null;
}

interface PpeTopProvisionedChartProps {
  items: TopProvisionedItem[];
}

const formatNumber = (value: number) =>
  Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const TopProvisionedTooltip = ({ active, payload }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const item: TopProvisionedItem = payload[0].payload;
  const unit = item.unit?.trim();

  return (
    <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-100">
      <div className="text-[13px] font-medium text-gray-900 mb-2">{item.name}</div>
      <div className="space-y-1.5 text-[13px]">
        <div className="flex items-center justify-between gap-6">
          <span className="text-gray-500">Quantity issued</span>
          <span className="font-medium text-gray-900">
            {formatNumber(item.totalQuantity)}
            {unit ? ` ${unit}` : ""}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-gray-500">Provisions</span>
          <span className="font-medium text-gray-900">
            {formatNumber(item.provisionCount)}
          </span>
        </div>
      </div>
    </div>
  );
};

const PpeTopProvisionedChart: React.FC<PpeTopProvisionedChartProps> = ({ items }) => {
  const data = items.slice(0, 5);

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-gray-900">Top Provisioned Items</span>
        <span className="text-xs text-gray-500">By quantity issued</span>
      </div>

      <div className="h-[240px] w-full">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-400">
            No provisions yet
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={data}
              margin={{ top: 0, right: 48, bottom: 0, left: 0 }}
              barCategoryGap={16}
            >
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#64748B" }}
                tickFormatter={(value: number) => formatNumber(value)}
              />
              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={CustomYAxisTick}
                width={180}
              />
              <Tooltip
                content={TopProvisionedTooltip}
                cursor={{ fill: "#F8FAFC" }}
                wrapperStyle={{ outline: "none" }}
              />
              <Bar
                dataKey="totalQuantity"
                name="Quantity issued"
                fill="#bce3ff"
                barSize={20}
                radius={[4, 4, 4, 4]}
                animationDuration={500}
              >
                <LabelList
                  dataKey="totalQuantity"
                  position="right"
                  formatter={(value: number) => formatNumber(value)}
                  style={{ fontSize: 12, fill: "#475569" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default PpeTopProvisionedChart;
